import analytics from './analytics';
import * as events from './analytics/events';

export type ABTestChangeEvent = {
  testId: string;
  bucketId: string | undefined;
  previousBucketId: string | undefined;
  newUsersOnly: boolean;
};

/**
 * Defines a source for external AB tests.
 */
export type ExternalABTestsSource = {
  /** @returns a map with test ids as keys, and bucket ids as values. */
  getABTests(): Partial<{ [testId: string]: string }>;
  /** @returns an [AB tag](https://docs.dev.gc-internal.net/gcinstant/ab-tests/index.html#ab-tag). */
  getABTag(): string;
  /** Should accept a handler to be called whenever the return values of getABTests or getABTag change. */
  setOnChangedHandler(
    handler: (changeEvents?: ABTestChangeEvent[]) => void, // parameter is optional for backwards-compatibility
  ): void;
};

export class ExternalABTests {
  private source?: ExternalABTestsSource;

  constructor(
    private opts: {
      getInternalABTag: () => string;
      getInternalABTestIDs: () => string[];
    },
  ) {}

  setSource(source: ExternalABTestsSource): void {
    if (this.source) {
      throw new Error('ExternalABTestsSource already set!');
    }

    this.source = source;

    this.updateUserProperties();
    this.source.setOnChangedHandler((...args) => this.onChanged(...args));
  }

  getPayloadProperties(): { [key: string]: string } {
    if (!this.source) {
      return {};
    }

    return this.getTests('sourceAB_');
  }

  getABTag(): string {
    return this.source?.getABTag() || '';
  }

  private onChanged(changeEvents?: ABTestChangeEvent[]): void {
    // Track ABTestAssigned for each new bucket assignment:
    for (const changeEvent of changeEvents || []) {
      if (changeEvent.bucketId) {
        events.trackABTestAssigned({
          bucket: changeEvent.bucketId,
          newPlayersOnly: changeEvent.newUsersOnly,
          test: changeEvent.testId,
        });
      }
    }

    this.updateUserProperties();
  }

  private updateUserProperties(): void {
    const internalABTag = this.opts.getInternalABTag();
    const externalABTag = this.source.getABTag();

    const props = {
      ABtag:
        internalABTag && externalABTag
          ? `${internalABTag},${externalABTag}`
          : internalABTag || externalABTag,
      ...this.getTests('abTest_'),
    };

    analytics.setUserProperties(props, analytics.toDefault);
  }

  private getTests(prefix: string) {
    const externalTests = this.source.getABTests();
    const internalTests = this.opts.getInternalABTestIDs();

    const result: { [key: string]: string } = {};

    for (const testId in externalTests) {
      if (internalTests.includes(testId)) {
        throw new Error(
          `Test id '${testId}' is present in both internal and external tests!`,
        );
      }

      const bucketId = externalTests[testId];
      if (!bucketId) {
        continue;
      }

      result[prefix + testId] = bucketId;
    }

    return result;
  }
}
